<template lang="html">
    <span v-if="canRelaunch" class="sl-relaunch">
        <b-button variant="primary" size="sm" :disabled="busy || !hasServableOffers" :title="disabledReason" @click="relaunch()">
            <span>RE-LAUNCH</span>
        </b-button>

        <b-button variant="outline-primary" size="sm" :disabled="busy" :title="hasServableOffers ? 'Choose placements' : 'View placements'" @click="openAdvanced()">
            <span>...</span>
        </b-button>

        <b-modal
            v-model="showAdvanced"
            size="lg"
            :title="modalTitle"
            ok-title="Re-launch"
            cancel-title="Close"
            :ok-disabled="!hasServableOffers || selectedPlacementIds.length === 0"
            @ok="relaunch(selectedPlacementIds)"
        >
            <p v-if="items.length === 0">Nothing was saved for this Smartlink.</p>

            <template v-else>
                <p v-if="!hasServableOffers" class="discard-warning">
                    {{ disabledReason }}, so it cannot be re-launched yet.
                    Raise an offer weight above 0 to enable Re-launch.
                </p>

                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th v-if="hasServableOffers">
                                <input type="checkbox" :checked="allSelected" @change="toggleAll($event.target.checked)">
                            </th>
                            <th>Placement ID</th>
                            <th>External ID</th>
                            <th>Name</th>
                            <th>Pub ID</th>
                            <th>Offer ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in items" :key="item.placement_id + '-' + item.offerid">
                            <td v-if="hasServableOffers">
                                <input type="checkbox" :value="item.placement_id" v-model="selectedPlacementIds">
                            </td>
                            <td>{{ item.placement_id }}</td>
                            <td>{{ item.placement_external_id }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.pid }}</td>
                            <td>{{ item.offerid }}</td>
                        </tr>
                    </tbody>
                </table>

                <p v-if="hasServableOffers && discardedCount > 0" class="discard-warning">
                    {{ discardedCount }} unchecked placement(s) will be discarded and cannot be restored later.
                </p>
            </template>
        </b-modal>
    </span>
</template>

<script>
export default {
    name: 'SlRelaunch',
    props: {
        sl: { type: Object, required: true }
    },
    data() {
        return {
            busy: false,
            showAdvanced: false,
            items: [],
            selectedPlacementIds: []
        }
    },
    computed: {
        canRelaunch() {
            return this.sl.snapshot_placements > 0
                && this.userHasPermission('smartlinks.status-change')
        },
        hasServableOffers() {
            return this.sl.active_offers > 0
        },
        disabledReason() {
            return this.hasServableOffers ? '' : 'Every offer in this Smartlink has a weight of 0'
        },
        modalTitle() {
            return this.hasServableOffers
                ? `Re-launch ${this.sl.name} on:`
                : `${this.sl.name} was removed from:`
        },
        placementIds() {
            return [...new Set(this.items.map(v => v.placement_id))]
        },
        allSelected() {
            return this.items.length > 0 && this.selectedPlacementIds.length === this.placementIds.length
        },
        discardedCount() {
            return this.placementIds.length - this.selectedPlacementIds.length
        }
    },
    watch: {
        'sl.status': function (status) {
            if (status === 'PAUSED') {
                this.syncSnapshotCount()
            } else {
                this.sl.snapshot_placements = 0
            }
        }
    },
    methods: {
        async loadSnapshot() {
            const r = await this.$http.get(this.resources.SmartLinksCampaigns.getCampaignPlacementSnapshot(this.sl.id))
            this.items = r.body.items || []
        },
        async syncSnapshotCount() {
            try {
                await this.loadSnapshot()
                this.sl.snapshot_placements = this.placementIds.length
            } catch (e) {
                this.items = []
            }
        },
        async openAdvanced() {
            this.busy = true
            try {
                await this.loadSnapshot()
                this.selectedPlacementIds = this.placementIds
                this.showAdvanced = true
            } finally {
                this.busy = false
            }
        },
        async relaunch(placementIds) {
            this.busy = true
            try {
                await this.$http.post(
                    this.resources.SmartLinksCampaigns.relaunchCampaign(this.sl.id),
                    placementIds ? { placement_ids: placementIds } : {}
                )
                this.sl.status = 'ACTIVE'
                this.sl.snapshot_placements = 0
                this.$emit('relaunched', this.sl)
                this.notifySuccess(`${this.sl.name} re-launched`)
            } catch (e) {
                this.notifyError(`Could not re-launch ${this.sl.name}`)
            } finally {
                this.busy = false
            }
        },
        toggleAll(checked) {
            this.selectedPlacementIds = checked ? this.placementIds : []
        }
    }
}
</script>

<style lang="css" scoped>
.sl-relaunch {
    display: inline-flex;
    gap: 4px;
}

.discard-warning {
    color: #d9534f;
    margin-bottom: 0;
}
</style>
